import { useState, useRef, useCallback, useEffect } from 'react';
import { Box, alpha } from '@mui/material';
import { CRM_PALETTE as P } from '../../theme/brandPalette';

/**
 * Barra de scroll de la PÁGINA, siempre visible y arrastrable.
 *
 * Hermana de `AlwaysScrollbar` (scrollers internos) y `DialogScrollbars` (modales),
 * para el único scroller que faltaba: el del documento.
 *
 * Por qué dibujada y no nativa: la nativa la decide el navegador. Chrome moderno usa
 * barras overlay que se desvanecen tras unos segundos, y macOS las esconde salvo que
 * el usuario cambie una preferencia del sistema. El pedido es que el indicador NUNCA
 * se oculte, así que no puede depender de ninguna de esas dos cosas.
 * (reportado por Cristian Avila, 29/07/2026 — Chrome/Windows, /properties/mine)
 *
 * Se monta una vez en el shell del CRM. Aparece solo cuando la página desborda, y se
 * esconde mientras hay un modal abierto: ahí el scroll de la página queda bloqueado y
 * la barra que manda es la del diálogo.
 */

/**
 * El scroller del documento. `scrollingElement` es null en quirks mode (y en algunos
 * entornos de test), y sin respaldo la barra no se dibujaba nunca: la función salía
 * antes de medir.
 */
function scroller(): HTMLElement | null {
  return (document.scrollingElement ?? document.documentElement) as HTMLElement | null;
}

/**
 * ¿Hay un diálogo abierto tapando la página? Ahí manda su propia barra, no esta.
 *
 * No alcanza con que el selector matchee: hay que confirmar que ese modal está
 * VISIBLE. Dos trampas del layout del CRM, las dos aprendidas a los golpes:
 *  1. el sidebar de escritorio es un Drawer PERMANENTE → `.MuiDrawer-root` a secas
 *     matchea siempre;
 *  2. el drawer de mobile usa `ModalProps={{ keepMounted: true }}` → sigue en el DOM
 *     estando cerrado, y ahí MUI solo le pone `visibility: hidden`.
 * Con cualquiera de las dos, un chequeo por selector da verdadero para siempre y la
 * barra no se dibuja nunca.
 *
 * Los menús y selects quedan afuera a propósito: aunque MUI bloquee el scroll mientras
 * están abiertos, son efímeros y hacer desaparecer la barra en cada desplegable
 * contradice el pedido de que esté siempre a la vista.
 */
function dialogoAbierto(): boolean {
  const nodos = document.querySelectorAll<HTMLElement>('.MuiDialog-root, .MuiDrawer-root.MuiModal-root');
  for (const n of Array.from(nodos)) {
    if (n.style.visibility === 'hidden') continue;                       // keepMounted cerrado
    if (typeof getComputedStyle === 'function') {
      const cs = getComputedStyle(n);
      if (cs.visibility === 'hidden' || cs.display === 'none') continue;
    }
    return true;
  }
  return false;
}

/**
 * Arranque vertical de la barra: justo debajo de la topbar fija. Se mide en vivo en vez
 * de hardcodear una altura porque arriba pueden aparecer banners (acceso a tenant,
 * suscripción, demo) que la corren para abajo.
 */
function topeSuperior(): number {
  const header = document.querySelector('header.MuiAppBar-root');
  const bottom = header ? header.getBoundingClientRect().bottom : 0;
  return Math.max(6, bottom + 6);
}

export function PageScrollbar() {
  const [m, setM] = useState({ thumbH: 0, thumbTop: 0, top: 6, visible: false });
  const drag = useRef<{ startY: number; startScroll: number } | null>(null);

  const recompute = useCallback(() => {

    const el = scroller();
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    if (scrollHeight <= clientHeight + 1 || dialogoAbierto()) {
      setM((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }
    const top = topeSuperior();
    const trackH = clientHeight - top - 6;
    const thumbH = Math.max(36, (clientHeight / scrollHeight) * trackH);
    const maxTop = trackH - thumbH;
    const thumbTop = ((scrollTop / (scrollHeight - clientHeight)) || 0) * maxTop;
    setM({ thumbH, thumbTop, top, visible: true });
  }, []);

  useEffect(() => {
    // El scroll del documento NO se emite en <html>: se escucha en window/document.
    // Por eso este componente no reusa `watchScroller` (que sí sirve para elementos).
    let raf = 0;
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(recompute); };

    recompute();
    window.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', schedule);

    // El alto de la página cambia sin que haya scroll: al navegar entre pantallas, al
    // llegar datos del server, al terminar de cargar las fotos de un listado.
    const ro = new ResizeObserver(schedule);
    ro.observe(document.body);
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true });
    const onLoad = (e: Event) => { if (e.target instanceof HTMLImageElement) schedule(); };
    document.addEventListener('load', onLoad, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
      mo.disconnect();
      document.removeEventListener('load', onLoad, true);
    };
  }, [recompute]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = drag.current;
      const el = scroller();
      if (!d || !el) return;
      const trackH = el.clientHeight - topeSuperior() - 6;
      const thumbH = Math.max(36, (el.clientHeight / el.scrollHeight) * trackH);
      const maxTop = trackH - thumbH;
      const range = el.scrollHeight - el.clientHeight;
      el.scrollTop = d.startScroll + ((e.clientY - d.startY) / maxTop) * range;
    };
    const onUp = () => { if (drag.current) { drag.current = null; document.body.style.userSelect = ''; } };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  if (!m.visible) return null;

  return (
    <Box sx={{
      position: 'fixed', top: m.top, right: 3, bottom: 6, width: 12,
      // Debajo de modales (1300) y por encima del contenido y del drawer.
      zIndex: 1250,
      pointerEvents: 'none',
    }}>
      <Box
        onMouseDown={(e) => {
          const el = scroller();
          if (!el) return;
          drag.current = { startY: e.clientY, startScroll: el.scrollTop };
          document.body.style.userSelect = 'none';
          e.preventDefault();
        }}
        sx={{
          position: 'absolute', right: 0, width: 9, borderRadius: 5,
          top: m.thumbTop, height: m.thumbH,
          bgcolor: alpha(P.brand.golden, 0.6),
          '&:hover': { bgcolor: P.brand.golden, width: 11 },
          cursor: 'grab',
          pointerEvents: 'auto',
          transition: 'background 0.15s ease, width 0.12s ease',
        }}
      />
    </Box>
  );
}

export default PageScrollbar;
